import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { logout } from "../../../store/authSlice";
import { fetchCategories } from "../../../store/categoriesSlice";
import "./Header.css";
import logoImage from "../../../assets/images/OmerToolsLogo.png";
import OmerToolsLogo from "../../../assets/images/logo512.png";
import { nameUrlUtils } from "../../Utils/nameUrlUtils";

import {
  FaShoppingCart,
  FaUser,
  FaSearch,
  FaBars,
  FaPhone,
  FaMapMarkerAlt,
  FaTruck,
  FaTimes,
  FaBox,
  FaTag,
  FaLayerGroup,
  FaChevronRight,
} from "react-icons/fa";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const Header = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const cartTotalQuantity = useSelector((state) => state.cart.totalQuantity);
  const categories = useSelector((state) => state.categories.categories);
  const categoriesStatus = useSelector((state) => state.categories.status);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [headerBanners, setHeaderBanners] = useState([]);
  const [activeBannerIdx, setActiveBannerIdx] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [apiError, setApiError] = useState("");
  const [showAllCategories, setShowAllCategories] = useState(false);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const navbarRef = useRef(null);
  const categoriesDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isMobileMenuOpen) return;

      // if navbarRef or one of its children contains the click, do nothing
      if (navbarRef.current && navbarRef.current.contains(event.target)) {
        return;
      }

      // otherwise, close menu
      setIsMobileMenuOpen(false);
      setActiveDropdown(null);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Fetch categories if not already loaded
  useEffect(() => {
    if (categoriesStatus === "idle") {
      dispatch(fetchCategories());
    }
  }, [categoriesStatus, dispatch]);

  // Fetch header promo banners
  useEffect(() => {
    const fetchHeaderBanners = async () => {
      try {
        const response = await fetch(`${BASE_URL}/site-settings/hero-images?section=header`);
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data) && data.length > 0) {
            const banners = data.map((img) => ({
              id: img.id,
              image: (process.env.REACT_APP_BASE_IMG_URL || '') + img.imageUrl,
              linkUrl: img.linkUrl || null,
              altText: img.altText || '',
            }));
            setHeaderBanners(banners);
          }
        }
      } catch {
        // No banner on error — silent fail
      }
    };
    fetchHeaderBanners();
  }, []);

  // Auto-rotate header banners
  useEffect(() => {
    if (headerBanners.length <= 1) return;
    const timer = setInterval(() => {
      setActiveBannerIdx((prev) => (prev + 1) % headerBanners.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [headerBanners]);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchSuggestions = async () => {
      const trimmedTerm = searchTerm.trim();
      if (trimmedTerm.length < 2) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      try {
        setIsLoading(true);
        setApiError("");

        const timestamp = Date.now();
        const response = await fetch(
          `${BASE_URL}/search/autocomplete?term=${encodeURIComponent(
            trimmedTerm,
          )}&t=${timestamp}`,
          { signal },
        );

        if (!response.ok) {
          throw new Error(
            `API error: ${response.status} ${response.statusText}`,
          );
        }

        const data = await response.json();

        // Normalize API response to ensure consistent casing
        const normalizedData = data.map((item) => ({
          Type: item.type || item.Type,
          Value: item.value || item.Value,
          Category: item.category || item.Category,
          ReferenceId: item.referenceId || item.ReferenceId,
        }));

        if (isMounted) {
          setSuggestions(normalizedData);
          setShowSuggestions(true);
        }
      } catch (error) {
        if (error.name !== "AbortError" && isMounted) {
          console.error("Error fetching suggestions:", error);
          setApiError("Failed to load suggestions. Please try again.");
          setSuggestions([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    const debounceTimer = setTimeout(fetchSuggestions, 300);

    return () => {
      isMounted = false;
      controller.abort();
      clearTimeout(debounceTimer);
    };
  }, [searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const trimmedTerm = searchTerm.trim();
    if (trimmedTerm) {
      // Forcefully hide the suggestions before navigating
      window.stop();
      const highestTimeoutId = setTimeout(() => {}, 0);
      for (let i = 0; i < highestTimeoutId; i++) {
        clearTimeout(i);
      }
      setShowSuggestions(false);
      // Blur the input to close dropdown visually
      if (searchRef.current) {
        const input = searchRef.current.querySelector("input");
        if (input) input.blur();
      }
      navigate(`/search?term=${encodeURIComponent(trimmedTerm)}`);
      setIsMobileMenuOpen(false);
      setShowSuggestions(false);
    }
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isMobileMenuOpen) return;

      if (navbarRef.current && navbarRef.current.contains(event.target)) {
        return;
      }

      setIsMobileMenuOpen(false);
      setActiveDropdown(null);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Handle click outside to close categories dropdown
  useEffect(() => {
    const handleClickOutsideCategories = (event) => {
      if (activeDropdown !== "categories") return;

      // If click is inside the categories dropdown or on the toggle button, do nothing
      if (
        categoriesDropdownRef.current &&
        categoriesDropdownRef.current.contains(event.target)
      ) {
        return;
      }

      // Otherwise, close the dropdown
      setActiveDropdown(null);
    };

    document.addEventListener("mousedown", handleClickOutsideCategories);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideCategories);
    };
  }, [activeDropdown]);
  const handleSuggestionClick = (suggestion) => {
    if (!suggestion) return;

    // Use normalized properties
    switch (suggestion.Type) {
      case "Product":
        navigate(`/product/${suggestion.ReferenceId}`);
        break;
      case "Brand":
        navigate(`/brand/${suggestion.ReferenceId}`);
        break;
      case "Category":
        navigate(`/category/${suggestion.ReferenceId}`);
        break;
      default:
        navigate(`/search?term=${encodeURIComponent(suggestion.Value)}`);
    }

    setShowSuggestions(false);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setSuggestions([]);
    setShowSuggestions(false);
    setApiError("");
  };

  const handleInputFocus = () => {
    if (searchTerm.trim().length >= 2) {
      setShowSuggestions(true);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const handleUserProfile = () => {
    navigate("/profile");
  };

  const toggleDropdown = (dropdownName) => {
    setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
  };

  const getSuggestionIcon = (type) => {
    switch (type) {
      case "Product":
        return <FaBox className="suggestion-icon product" />;
      case "Brand":
        return <FaTag className="suggestion-icon brand" />;
      case "Category":
        return <FaLayerGroup className="suggestion-icon category" />;
      default:
        return <FaSearch className="suggestion-icon default" />;
    }
  };

  // Sort categories alphabetically by name
  const sortedCategories = [...categories].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  // Calculate categories to display
  const displayedCategories = showAllCategories
    ? sortedCategories
    : sortedCategories.slice(0, 10);

  return (
    <header className="header">
      {/* Header Promo Banner — only shown on homepage */}
      {location.pathname === "/" && headerBanners.length > 0 && (
        <div className="header-promo-banner">
          {headerBanners.map((banner, idx) => (
            <div
              key={banner.id}
              className={`header-promo-slide ${idx === activeBannerIdx ? "active" : ""}`}
            >
              {banner.linkUrl ? (
                <Link to={banner.linkUrl}>
                  <img src={banner.image} alt={banner.altText} />
                </Link>
              ) : (
                <img src={banner.image} alt={banner.altText} />
              )}
            </div>
          ))}
          {headerBanners.length > 1 && (
            <div className="header-promo-dots">
              {headerBanners.map((_, idx) => (
                <button
                  key={idx}
                  className={`header-promo-dot ${idx === activeBannerIdx ? "active" : ""}`}
                  onClick={() => setActiveBannerIdx(idx)}
                  aria-label={`Banner ${idx + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
      <div className="top-bar">
        <div className="container">
          <div className="top-bar-content">
            <div className="free-delivery-container">
              <span className="free-delivery">
                <FaTruck /> FREE delivery on orders over $100
              </span>
            </div>
            <div className="top-bar-left"></div>
            <div className="top-bar-right">
              <span>
                <a href="tel:0297598833" className="phone-link">
                  <FaPhone /> 02 9759 8833
                </a>
              </span>
              <span>
                <FaMapMarkerAlt />{" "}
                <Link to="/store-locations">Store Location</Link>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="main-header">
        <div className="container">
          <div className="main-header-grid">
            <button
              className="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <FaBars />
            </button>

            <Link to="/" className="logo">
              <img src={logoImage} alt="Omer Tools" />
            </Link>

            <div className="search-container-wrapper" ref={searchRef}>
              <form
                className="search-form"
                onSubmit={handleSearch}
                role="search"
              >
                <div className="search-input-container">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={handleInputFocus}
                    placeholder="Search products, brands, categories..."
                    className="search-input"
                    aria-label="Search"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      className="clear-search-btn"
                      onClick={handleClearSearch}
                      aria-label="Clear search"
                    >
                      <FaTimes />
                    </button>
                  )}
                  <button
                    type="submit"
                    className="search-btn"
                    aria-label="Submit search"
                  >
                    <FaSearch />
                  </button>
                </div>
              </form>

              {showSuggestions && (
                <div className="suggestions-dropdown">
                  {isLoading ? (
                    <div className="suggestion-item loading">
                      <div className="suggestion-loader"></div>
                      Loading suggestions...
                    </div>
                  ) : apiError ? (
                    <div className="suggestion-item error">
                      <div className="error-icon">!</div>
                      {apiError}
                    </div>
                  ) : suggestions.length > 0 ? (
                    <>
                      <div className="suggestion-header">
                        <span>Suggestions</span>
                        <span className="suggestion-count">
                          {suggestions.length} results
                        </span>
                      </div>
                      {suggestions.map((suggestion) => (
                        <div
                          key={`${suggestion.Type}-${suggestion.ReferenceId}`}
                          className="suggestion-item"
                          onClick={() => handleSuggestionClick(suggestion)}
                        >
                          {getSuggestionIcon(suggestion.Type)}
                          <div className="suggestion-content">
                            <div className="suggestion-main">
                              <span className="suggestion-value">
                                {suggestion.Value}
                              </span>
                              <span className="suggestion-type">
                                {suggestion.Type}
                              </span>
                            </div>
                            {suggestion.Category && (
                              <div className="suggestion-category">
                                {suggestion.Category}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </>
                  ) : searchTerm.trim().length >= 2 ? (
                    <div className="suggestion-item no-results">
                      No suggestions found for "{searchTerm}"
                    </div>
                  ) : null}
                </div>
              )}
            </div>
            <div className="user-cart-container">
              {isAuthenticated ? (
                <div className="user-dropdown">
                  <button className="user-btn">
                    <FaUser /> <span>{user?.name}</span>
                  </button>
                  <div className="user-dropdown-menu">
                    <button onClick={handleLogout} className="dropdown-item">
                      Logout
                    </button>
                    <button
                      onClick={handleUserProfile}
                      className="dropdown-item"
                    >
                      User Profile
                    </button>
                  </div>
                </div>
              ) : (
                <Link to="/login" className="login-btn">
                  <FaUser /> <span>Login</span>
                </Link>
              )}

              <Link to="/cart" className="cart-container1">
                <div className="cart-icon">
                  <FaShoppingCart />
                  <span className="cart-count">{cartTotalQuantity}</span>
                </div>
                <span className="cart-text">Cart</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
      {isMobileMenuOpen && (
        <div className="backdrop" onClick={() => setIsMobileMenuOpen(false)} />
      )}
      <nav
        ref={navbarRef}
        className={`navbar ${isMobileMenuOpen ? "open" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="container">
          {isMobileMenuOpen && (
            <button
              className="mobile-menu-close"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              <FaTimes />
            </button>
          )}
          <ul className="nav-list">
            <li
              ref={categoriesDropdownRef}
              className={`nav-item dropdown ${
                activeDropdown === "categories" ? "active" : ""
              }`}
              onClick={() => toggleDropdown("categories")}
            >
              <span className="dropdown-toggle">
                <FaBars className="menu-bars" /> Shop by Category
              </span>

              {activeDropdown === "categories" && (
                <div className="dropdown-menu">
                  <div className="category-grid">
                    {sortedCategories.map((category) => (
                      <Link
                        key={category.id}
                        to={`/category/${nameUrlUtils.convertNameToUrl(category.name)}`}
                        className="dropdown-item"
                        onClick={() => {
                          setActiveDropdown(null);
                          setIsMobileMenuOpen(false);
                        }}
                      >
                        {category.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </li>

            <li className="nav-item" onClick={() => setIsMobileMenuOpen(false)}>
              <Link to="/shop-by-brand">Shop By Brand</Link>
            </li>
            <li className="nav-item" onClick={() => setIsMobileMenuOpen(false)}>
              <Link to="/redemption">Redemptions</Link>
            </li>
            <li className="nav-item" onClick={() => setIsMobileMenuOpen(false)}>
              <Link to="/store-locations">Store Location</Link>
            </li>
            <li className="nav-item" onClick={() => setIsMobileMenuOpen(false)}>
              <Link to="/create-your-kit">
                Create Your Own Kit
              </Link>
            </li>
            <div className="mobile-only-links">
              <li
                className="nav-item"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Link to="/warranty-claim">Warranty Claim</Link>
              </li>
            </div>
            {/* <li className="nav-item" onClick={() => setIsMobileMenuOpen(false)}>
              <Link to="/blog">
               Blog
              </Link>
            </li> */}
            <div className="mobile-top-bar">
              <div className="mobile-top-bar-right">
                <span>
                  <a href="tel:0297598833" className="phone-link">
                    <FaPhone /> 02 9759 8833
                  </a>
                </span>
              </div>
            </div>
          </ul>
        </div>
      </nav>
    </header>
  );
};

export default Header;
